import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {IEngagementTabResponse, IUserDrilldownResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {SortIcon, sortTh} from "../shared/SortableTh.component";
import {fetchUserDrilldown, getCachedUserDrilldown} from "../shared/drilldownCache";
import {UserPlanDrilldownBlock} from "./UserPlanDrilldownBlock.component";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IPowerUsersTableProps {
    internalPowerUsers: IEngagementTabResponse["internalPowerUsers"];
    clientPowerUsers: IEngagementTabResponse["clientPowerUsers"];
    range: { startDate: string; endDate: string };
    country: string;
}

export const PowerUsersTable: React.FC<IPowerUsersTableProps> = (props) => {
    const [powerUserSearchText, setPowerUserSearchText] = useState<string>("");
    const [powerUserTab, setPowerUserTab] = useState<"internal" | "client">("internal");
    const [powerUserSort, setPowerUserSort] = useState<{ col: string; dir: SortDir }>({col: "visits", dir: "desc"});
    const [expandedUser, setExpandedUser] = useState<string | null>(null);
    const [userDrilldownData, setUserDrilldownData] = useState<IUserDrilldownResponse | null>(null);
    const [userDrilldownLoading, setUserDrilldownLoading] = useState<boolean>(false);
    // Guards against out-of-order responses: only the latest expand's fetch may update state.
    const requestId = useRef(0);

    const activePowerUsers = powerUserTab === "client" ? props.clientPowerUsers : props.internalPowerUsers;
    const filteredPowerUsers = useMemo(() => {
        const text = powerUserSearchText.trim().toLowerCase();
        const filtered = text
            ? activePowerUsers.filter((row) => row.user.toLowerCase().includes(text)
                || row.topArea.toLowerCase().includes(text))
            : activePowerUsers;
        return applySort(filtered, powerUserSort.col, powerUserSort.dir);
    }, [activePowerUsers, powerUserSearchText, powerUserSort]);

    // Warm the shared drilldown cache in the background so expanding a user is instant.
    useEffect(() => {
        const allUsers = [...props.internalPowerUsers, ...props.clientPowerUsers];
        if (allUsers.length === 0) return;
        let cancelled = false;
        const prefetch = async () => {
            for (const user of allUsers) {
                if (cancelled) break;
                if (getCachedUserDrilldown(user.user)) continue;
                try {
                    await fetchUserDrilldown(
                        user.user,
                        props.range.startDate,
                        props.range.endDate,
                        props.country || undefined);
                } catch { /* background fetch — ignore errors */
                }
            }
        };
        prefetch();
        return () => {
            cancelled = true;
        };
    }, [props.internalPowerUsers, props.clientPowerUsers, props.range.startDate, props.range.endDate, props.country]);

    const handleUserDrilldown = (userEmail: string) => {
        if (expandedUser === userEmail) {
            requestId.current++;
            setExpandedUser(null);
            setUserDrilldownData(null);
            return;
        }
        const id = ++requestId.current;
        setExpandedUser(userEmail);
        const cached = getCachedUserDrilldown(userEmail);
        if (cached) {
            setUserDrilldownData(cached);
            setUserDrilldownLoading(false);
            return;
        }
        setUserDrilldownLoading(true);
        setUserDrilldownData(null);
        fetchUserDrilldown(userEmail, props.range.startDate, props.range.endDate, props.country || undefined)
            .then((data) => {
                if (id !== requestId.current) return;
                setUserDrilldownData(data);
                setUserDrilldownLoading(false);
            })
            .catch(() => {
                if (id !== requestId.current) return;
                setUserDrilldownData(null);
                setUserDrilldownLoading(false);
            });
    };

    const switchTab = (tab: "internal" | "client") => {
        setPowerUserTab(tab);
        setPowerUserSearchText("");
        setExpandedUser(null);
        setUserDrilldownData(null);
    };

    return (
        <section className="admin-analytics__section" data-testid={"power-users"}>
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Power Users</h2>
                <span className="admin-analytics__section-subtitle">Top users by activity in selected period</span>
            </div>
            <p className="admin-analytics__section-desc">
                Identifies the individuals with the most page visits during the selected period. Power users
                are your
                strongest advocates and the best source of feedback. Use the Internal / Client tabs to see
                each group
                separately — a healthy client power user list indicates strong external adoption. Click any
                user
                to drill down and see their activity broken out by plan with the top pages in each.
            </p>
            <div className="admin-analytics__subtabs">
                <button
                    className={`admin-analytics__subtab${
                        powerUserTab === "internal" ? " admin-analytics__subtab--active" : ""}`}
                    onClick={() => switchTab("internal")}
                >
                    Internal ({props.internalPowerUsers.length})
                </button>
                <button
                    className={`admin-analytics__subtab${
                        powerUserTab === "client" ? " admin-analytics__subtab--active" : ""}`}
                    onClick={() => switchTab("client")}
                >
                    Client ({props.clientPowerUsers.length})
                </button>
            </div>
            <div className="admin-analytics__table-controls">
                <input className="admin-analytics__input" placeholder="Filter users"
                       value={powerUserSearchText}
                       onChange={(e) => setPowerUserSearchText(e.target.value)}/>
                <div className="admin-analytics__count-label">
                    Showing {filteredPowerUsers.length} of {activePowerUsers.length} users
                </div>
            </div>
            <table className="admin-analytics__table admin-analytics__table--fixed"
                   style={{tableLayout: "fixed"}}>
                <colgroup>
                    <col style={{width: "40%"}}/>
                    <col style={{width: "100px"}}/>
                    <col/>
                </colgroup>
                <thead>
                <tr>
                    {sortTh("user", "User", powerUserSort, setPowerUserSort, undefined, "asc")}
                    {sortTh("visits", "Visits", powerUserSort, setPowerUserSort, "admin-analytics__cell--right")}
                    <th
                        className="admin-analytics__th--sortable"
                        onClick={() => setPowerUserSort({
                            col: "topArea",
                            dir: powerUserSort.col === "topArea"
                                ? (powerUserSort.dir === "desc" ? "asc" : "desc")
                                : "asc",
                        })}
                        style={{paddingLeft: "50px"}}
                        tabIndex={0}
                        role="columnheader"
                        aria-sort={powerUserSort.col === "topArea"
                            ? (powerUserSort.dir === "asc" ? "ascending" : "descending")
                            : undefined}
                    >
                        Top Area <SortIcon col="topArea" active={powerUserSort.col}
                                           dir={powerUserSort.dir}/>
                    </th>
                </tr>
                </thead>
                <tbody>
                {filteredPowerUsers.length === 0 ? (
                    <tr>
                        <td colSpan={3} className="admin-analytics__empty-row">No {powerUserTab} users found
                            in this period.
                        </td>
                    </tr>
                ) : filteredPowerUsers.map((row) => (
                    <React.Fragment key={`${row.user}-${row.topArea}`}>
                        <tr
                            className={`admin-analytics__row--expandable${
                                expandedUser === row.user ? " admin-analytics__row--expanded" : ""}`}
                            onClick={() => handleUserDrilldown(row.user)}
                            title="Click to see activity by plan"
                        >
                            <td>
                                <i className={`far ${
                                    expandedUser === row.user ? "fa-chevron-down" : "fa-chevron-right"
                                } admin-analytics__expand-icon`}
                                   aria-hidden="true"/>
                                {row.user}
                            </td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(row.visits)}</td>
                            <td style={{paddingLeft: "50px"}}>{row.topArea}</td>
                        </tr>
                        {expandedUser === row.user && (
                            <tr className="admin-analytics__drilldown-row">
                                <td colSpan={3}>
                                    {userDrilldownLoading && (
                                        <div className="admin-analytics__drilldown-loading"><span
                                            className="admin-analytics__inline-spinner"/> Loading user
                                            activity...</div>
                                    )}
                                    {!userDrilldownLoading && userDrilldownData
                                        && userDrilldownData.plans.length > 0 && (
                                            <div className="admin-analytics__drilldown">
                                                <div className="admin-analytics__drilldown-summary">
                                                <span>
                                                    <strong>
                                                        {numberWithCommas(userDrilldownData.totalPageViews)}
                                                    </strong>
                                                    {" "}total page views
                                                </span>
                                                    <span>
                                                    <strong>{userDrilldownData.plansAccessed}</strong>
                                                        {" "}plans accessed
                                                </span>
                                                </div>
                                                {userDrilldownData.plans.map((plan) => (
                                                    <UserPlanDrilldownBlock key={plan.planName} plan={plan}/>
                                                ))}
                                            </div>
                                        )}
                                    {!userDrilldownLoading && userDrilldownData
                                        && userDrilldownData.plans.length === 0 && (
                                            <div className="admin-analytics__drilldown-empty">No page-level data
                                                available for this user.</div>
                                        )}
                                    {!userDrilldownLoading && !userDrilldownData && (
                                        <div className="admin-analytics__drilldown-empty">Could not load
                                            user activity data.</div>
                                    )}
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                ))}
                </tbody>
            </table>
        </section>
    );
};
